/**
 * The batch: from picked files to ordered jobs.
 *
 * Two rules shape this, both from how the tool is actually used -- pick twenty
 * songs, walk away, come back to the results:
 *
 * * A file that fails does not stop the others. Returning to eighteen results
 *   and a note about two beats returning to nothing.
 * * A *systemic* failure does stop them. If the GPU went off or the session
 *   died, the next nineteen attempts would fail identically and bury the one
 *   message that matters.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { api, ApiError, type JobOrder } from '../api/client'
import { checkAudioFile } from '../lib/audioFile'
import { uploadFile, UploadAborted, type UploadHandle } from '../upload/uploadFile'

/** Matches the server's per-user cap on uploads in flight. */
const MAX_CONCURRENT_UPLOADS = 3

export type ItemStatus =
  'checking' | 'rejected' | 'queued' | 'uploading' | 'uploaded' | 'ordering' | 'ordered' | 'failed'

export interface BatchItem {
  id: string
  file: File
  status: ItemStatus
  progress: number
  reason?: string
  durationSeconds?: number | null
  uploadId?: string
  jobId?: string
}

let counter = 0
const nextId = () => `item-${(counter += 1)}`

/** A failure that will hit every remaining file the same way. */
function isSystemic(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  // 503 the GPU is off, 501 power control missing, 401 session gone,
  // 0 the server is unreachable, 5xx it is broken.
  return error.status === 0 || error.status === 401 || error.status === 429 || error.status >= 500
}

function messageOf(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

export function useBatch() {
  const [items, setItems] = useState<BatchItem[]>([])
  const [uploading, setUploading] = useState(false)
  const [ordering, setOrdering] = useState(false)
  const [stopped, setStopped] = useState<string | null>(null)
  const handles = useRef(new Map<string, UploadHandle>())
  const aborter = useRef<AbortController | null>(null)

  // Stop uploading when whoever owns this batch goes away -- navigating from
  // Order to Queue mid-upload, for instance. The chunks would otherwise keep
  // going while the state needed to *order* them has been thrown away with the
  // component, so they could never become jobs: bandwidth spent to leave
  // orphaned uploads on the server until the sweep collects them. It also keeps
  // "is work in flight" honest, which is what the reload prompt reads.
  useEffect(() => () => aborter.current?.abort(), [])

  const patch = useCallback((id: string, changes: Partial<BatchItem>) => {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...changes } : item)))
  }, [])

  const runUploads = useCallback(
    async (queued: BatchItem[]) => {
      setUploading(true)
      setStopped(null)
      const controller = new AbortController()
      aborter.current = controller

      const pending = [...queued]
      const worker = async () => {
        for (;;) {
          if (controller.signal.aborted) return
          const item = pending.shift()
          if (!item) return

          patch(item.id, { status: 'uploading', progress: 0 })
          const handle: UploadHandle = {}
          handles.current.set(item.id, handle)
          try {
            const upload = await uploadFile(item.file, {
              signal: controller.signal,
              handle,
              onProgress: (fraction) => patch(item.id, { progress: fraction }),
            })
            patch(item.id, { status: 'uploaded', progress: 1, uploadId: upload.id })
          } catch (cause) {
            if (cause instanceof UploadAborted) return
            patch(item.id, {
              status: 'failed',
              reason: messageOf(cause, 'The upload failed.'),
            })
            if (isSystemic(cause)) {
              controller.abort()
              setStopped(
                messageOf(cause, 'The server stopped responding, so the rest were not uploaded.'),
              )
              return
            }
          }
        }
      }

      await Promise.all(
        Array.from({ length: Math.min(MAX_CONCURRENT_UPLOADS, pending.length) }, worker),
      )
      setUploading(false)
      aborter.current = null
    },
    [patch],
  )

  const addFiles = useCallback(
    async (files: File[]) => {
      const fresh: BatchItem[] = files.map((file) => ({
        id: nextId(),
        file,
        status: 'checking',
        progress: 0,
      }))
      setItems((current) => [...current, ...fresh])

      // Check the bytes before anything is uploaded. The server checks too and
      // has the final say, but a file that was never going to be accepted
      // should not cross the network first.
      const accepted: BatchItem[] = []
      for (const item of fresh) {
        const check = await checkAudioFile(item.file)
        if (check.ok) {
          const ready = {
            ...item,
            status: 'queued' as const,
            durationSeconds: check.durationSeconds ?? null,
          }
          accepted.push(ready)
          patch(item.id, ready)
        } else {
          patch(item.id, { status: 'rejected', reason: check.reason })
        }
      }

      if (accepted.length > 0) await runUploads(accepted)
    },
    [patch, runUploads],
  )

  const cancelUploads = useCallback(() => {
    aborter.current?.abort()
    setUploading(false)
    setItems((current) =>
      current.map((item) =>
        item.status === 'uploading' || item.status === 'queued'
          ? { ...item, status: 'failed', reason: 'Cancelled.' }
          : item,
      ),
    )
  }, [])

  const remove = useCallback((id: string) => {
    setItems((current) => current.filter((item) => item.id !== id))
  }, [])

  const reset = useCallback(() => {
    setItems([])
    setStopped(null)
  }, [])

  /** Submit every uploaded file as its own order. */
  const order = useCallback(
    async (options: JobOrder) => {
      setOrdering(true)
      setStopped(null)
      const ready = items.filter((item) => item.status === 'uploaded' && item.uploadId)

      for (const item of ready) {
        patch(item.id, { status: 'ordering' })
        try {
          const job = await api.submitJob(item.uploadId as string, options)
          patch(item.id, { status: 'ordered', jobId: job.id })
        } catch (cause) {
          patch(item.id, { status: 'failed', reason: messageOf(cause, 'Could not order this.') })
          if (isSystemic(cause)) {
            setStopped(messageOf(cause, 'Ordering stopped; the rest were left untouched.'))
            break
          }
        }
      }
      setOrdering(false)
    },
    [items, patch],
  )

  return {
    items,
    uploading,
    ordering,
    stopped,
    addFiles,
    cancelUploads,
    remove,
    reset,
    order,
  }
}
